import { HttpClient } from '@adapters/HttpClient';
import { EntriesApiResponse, Entry } from '../models/Entry';

export interface EntryFilters {
  search?: string;
  team_id?: number;
  product_ids?: number[];
  status?: string[];
  page?: number;
  per_page?: number;
}

/**
 * Adapter para comunicación con la API de Entradas
 */
export class EntryApiAdapter {
  private readonly basePath = '/entries';

  constructor(private httpClient: HttpClient) {}

  /**
   * Detalle de una entrada por ID (p. ej. referencia para paquete identificado INBOUND)
   */
  async getEntry(id: number): Promise<Entry> {
    return await this.httpClient.get<Entry>(`${this.basePath}/${id}`, {}, {}, false);
  }

  /**
   * Construye los parámetros de advanced_filters según los filtros proporcionados
   */
  private buildAdvancedFiltersParams(filters: EntryFilters): Record<string, any> {
    const advancedFilters: any[] = [];

    // Filtro por búsqueda (reference contains)
    if (filters.search && filters.search.trim()) {
      advancedFilters.push({
        column: 'reference',
        operator: 'contains',
        value: filters.search.trim(),
      });
    }

    // Filtro por cliente (team_id in)
    if (filters.team_id) {
      advancedFilters.push({
        column: 'team_id',
        operator: 'in',
        value: [filters.team_id], // El operador 'in' requiere un array
      });
    }

    // Filtro por estado (status in)
    if (filters.status && filters.status.length > 0) {
      advancedFilters.push({
        column: 'status',
        operator: 'in',
        value: filters.status,
      });
    }

    // Si hay filtros avanzados, construir los parámetros
    if (advancedFilters.length > 0) {
      const params: Record<string, any> = {
        'advanced_filters[logic]': 'AND',
      };

      advancedFilters.forEach((filter, index) => {
        params[`advanced_filters[filters][${index}][column]`] = filter.column;
        params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

        // Para operadores 'in' y 'not_in', enviar el valor como array
        if (filter.operator === 'in' || filter.operator === 'not_in') {
          const values = Array.isArray(filter.value) ? filter.value : [filter.value];
          values.forEach((val, valIndex) => {
            params[`advanced_filters[filters][${index}][value][${valIndex}]`] = val;
          });
        } else {
          // Para otros operadores, enviar el valor directamente
          params[`advanced_filters[filters][${index}][value]`] = filter.value;
        }
      });

      return params;
    }

    return {};
  }

  /**
   * Obtiene lista paginada de entradas
   */
  async getEntries(per_page = 50, page = 1, filters?: EntryFilters): Promise<EntriesApiResponse> {
    const params: Record<string, any> = {
      page,
      per_page,
    };

    // Agregar advanced_filters si hay filtros de búsqueda, cliente o estado
    if (filters && (filters.search || filters.team_id || filters.status)) {
      const advancedFiltersParams = this.buildAdvancedFiltersParams(filters);
      Object.assign(params, advancedFiltersParams);
    }

    // product_ids se envía como param directo (ej: product_ids=10,20,30)
    if (filters?.product_ids && filters.product_ids.length > 0) {
      params['product_ids'] = filters.product_ids.join(',');
    }

    const response = await this.httpClient.get<EntriesApiResponse>(this.basePath, params, {}, false);

    return response;
  }
}
